import { useEffect } from 'react'
import { Alert, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { fetchCostCenters } from '@/features/finance/financeApi'
import {
  STAKEHOLDER_KINDS,
  STAKEHOLDER_TYPES,
  createStakeholder,
  percentToBp,
  updateStakeholder,
  type Stakeholder,
  type StakeholderDraft,
  type StakeholderKind,
  type StakeholderType,
} from './stakeholderApi'

const COMPANY = 'COMPANY'

interface StakeholderFormModalProps {
  open: boolean
  stakeholder: Stakeholder | null
  onClose: () => void
  onSaved: (saved: Stakeholder) => void
}

interface FormValues {
  kind: StakeholderKind
  stakeholderType: StakeholderType
  name: string
  phone?: string
  whatsapp?: string
  email?: string
  address?: string
  idDocument?: string
  bankName?: string
  bankAccount?: string
  paymentDetails?: string
  agreementRef?: string
  startDate: Dayjs
  notes?: string
  scope: string
  sharePercent: number
}

const text = (value: string | undefined): string | null => (value?.trim() ? value.trim() : null)

export function StakeholderFormModal({ open, stakeholder, onClose, onSaved }: StakeholderFormModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const creating = stakeholder === null

  useEffect(() => {
    if (!open) return
    if (stakeholder) {
      form.setFieldsValue({
        kind: stakeholder.kind,
        stakeholderType: stakeholder.stakeholderType,
        name: stakeholder.name,
        phone: stakeholder.phone ?? '',
        whatsapp: stakeholder.whatsapp ?? '',
        email: stakeholder.email ?? '',
        address: stakeholder.address ?? '',
        idDocument: stakeholder.idDocument ?? '',
        bankName: stakeholder.bankName ?? '',
        bankAccount: stakeholder.bankAccount ?? '',
        paymentDetails: stakeholder.paymentDetails ?? '',
        agreementRef: stakeholder.agreementRef ?? '',
        startDate: dayjs(stakeholder.startDate),
        notes: stakeholder.notes ?? '',
      })
    } else {
      form.setFieldsValue({
        kind: 'INDIVIDUAL',
        stakeholderType: 'INVESTOR',
        startDate: tenantToday(),
        scope: COMPANY,
        sharePercent: 0,
      })
    }
  }, [open, stakeholder, form])

  const centers = useQuery({
    queryKey: queryKeys.finance.costCenters,
    queryFn: ({ signal }) => fetchCostCenters(signal),
    enabled: open && creating,
  })

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft: StakeholderDraft = {
        kind: values.kind,
        stakeholderType: values.stakeholderType,
        name: values.name.trim(),
        phone: text(values.phone),
        whatsapp: text(values.whatsapp),
        email: text(values.email),
        address: text(values.address),
        idDocument: text(values.idDocument),
        bankName: text(values.bankName),
        bankAccount: text(values.bankAccount),
        paymentDetails: text(values.paymentDetails),
        agreementRef: text(values.agreementRef),
        startDate: values.startDate.format('YYYY-MM-DD'),
        notes: text(values.notes),
      }
      return stakeholder
        ? updateStakeholder(stakeholder.id, draft)
        : createStakeholder(
            draft,
            values.scope === COMPANY ? null : values.scope,
            percentToBp(values.sharePercent),
          )
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      onSaved(saved)
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={creating ? t('stakeholder.form.createTitle') : t('stakeholder.form.editTitle', { code: stakeholder.code })}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      destroyOnHidden
      width={760}
    >
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="kind" label={t('stakeholder.field.kind')}>
              <Select options={STAKEHOLDER_KINDS.map((value) => ({ value, label: t(`stakeholder.kind.${value}`) }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="stakeholderType" label={t('stakeholder.field.type')}>
              <Select options={STAKEHOLDER_TYPES.map((value) => ({ value, label: t(`stakeholder.type.${value}`) }))} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="name"
              label={t('stakeholder.field.name')}
              rules={[{ required: true, whitespace: true, message: t('stakeholder.validation.name') }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          {creating ? (
            <>
              <Col xs={24} md={12}>
                <Form.Item name="scope" label={t('stakeholder.field.scope')} extra={t('stakeholder.field.scopeHint')}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={centers.isPending}
                    options={[
                      { value: COMPANY, label: t('stakeholder.scope.company') },
                      ...(centers.data ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${c.displayName}` })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sharePercent"
                  label={t('stakeholder.field.share')}
                  extra={t('stakeholder.field.shareHint')}
                  rules={[{ required: true, message: t('stakeholder.validation.share') }]}
                >
                  <InputNumber min={0} max={100} step={0.01} precision={2} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </>
          ) : null}
          <Col xs={24} md={12}>
            <Form.Item name="phone" label={t('stakeholder.field.phone')}>
              <Input maxLength={32} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="whatsapp" label={t('stakeholder.field.whatsapp')}>
              <Input maxLength={32} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label={t('stakeholder.field.email')} rules={[{ type: 'email', message: t('stakeholder.validation.email') }]}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="idDocument" label={t('stakeholder.field.idDocument')}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="address" label={t('stakeholder.field.address')}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="bankName" label={t('stakeholder.field.bankName')}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="bankAccount" label={t('stakeholder.field.bankAccount')}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="paymentDetails" label={t('stakeholder.field.paymentDetails')}>
              <Input.TextArea rows={2} maxLength={1000} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="agreementRef" label={t('stakeholder.field.agreementRef')}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="startDate" label={t('stakeholder.field.startDate')} rules={[{ required: true, message: t('stakeholder.validation.startDate') }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label={t('stakeholder.field.notes')}>
              <Input.TextArea rows={2} maxLength={2000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
